import { useState } from 'react';
import api from '../../utils/api';

/**
 * TranslateButton — auto-translates English text to Arabic using MyMemory API.
 *
 * Usage in any admin form:
 *
 *   <TranslateButton
 *     sourceText={form.title}
 *     onTranslated={(arabic) => set('title_ar', arabic)}
 *   />
 *
 * Props:
 *   sourceText   — the English text to translate
 *   onTranslated — callback(translatedArabic: string)
 *   label        — optional button label (default: "Auto-translate to Arabic")
 *   className    — optional extra classes
 */
export default function TranslateButton({
  sourceText,
  onTranslated,
  label = 'Translate to Arabic',
  className = '',
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  const handleTranslate = async () => {
    const text = (sourceText || '').trim();
    if (!text) {
      setError('Nothing to translate. Fill in the English field first.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const { data } = await api.post('/translate', { text, from: 'en', to: 'ar' });
      if (data.success && data.translated) {
        onTranslated(data.translated);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('Translation failed. Please type Arabic manually.');
        setTimeout(() => setError(''), 4000);
      }
    } catch (err) {
      setError('Translation service unavailable. Please type manually.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <button
        type="button"
        onClick={handleTranslate}
        disabled={loading || !sourceText?.trim()}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${
          success
            ? 'bg-green-50 border-green-300 text-green-700'
            : loading
            ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
            : !sourceText?.trim()
            ? 'bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed'
            : 'bg-cyan-50 border-cyan-300 text-cyan-700 hover:bg-cyan-100 hover:border-cyan-400'
        }`}
        title={!sourceText?.trim() ? 'Fill in the English field first' : 'Auto-translate to Arabic'}
      >
        {loading ? (
          <>
            <div className="h-3 w-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
            Translating...
          </>
        ) : success ? (
          <>✅ Translated!</>
        ) : (
          <>
            <span style={{ fontFamily: 'Amiri, serif', fontSize: '13px' }}>ع</span>
            {label}
          </>
        )}
      </button>

      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}

/**
 * ArabicField — a labelled Arabic text input/textarea with a TranslateButton.
 * Drop-in replacement for manual Arabic fields in admin forms.
 *
 * Usage:
 *   <ArabicField
 *     label="Title (Arabic)"
 *     value={form.title_ar}
 *     onChange={(val) => set('title_ar', val)}
 *     sourceText={form.title}        ← the English text to translate from
 *     multiline={false}              ← true for textarea
 *     rows={3}                       ← only used when multiline=true
 *   />
 */
export function ArabicField({
  label,
  value,
  onChange,
  sourceText,
  multiline = false,
  rows = 3,
  placeholder = 'اكتب النص بالعربية هنا أو اضغط ترجمة...',
  required = false,
}) {
  const arabicStyle = {
    fontFamily: "'Amiri', 'Traditional Arabic', serif",
    direction: 'rtl',
    fontSize: '16px',
    lineHeight: '2',
  };

  return (
    <div>
      {/* Label row with translate button */}
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
        <label className="form-label mb-0">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
          <span className="ml-2 text-gray-400 font-normal text-xs arabic" style={{ fontFamily: 'Amiri, serif' }}>
            (عربي)
          </span>
        </label>
        <TranslateButton
          sourceText={sourceText}
          onTranslated={onChange}
        />
      </div>

      {/* Input or textarea */}
      {multiline ? (
        <textarea
          rows={rows}
          required={required}
          className="form-input resize-none"
          style={arabicStyle}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <input
          required={required}
          className="form-input"
          style={arabicStyle}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}
